import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  Typography,
  Hidden,
  Snackbar,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import Layout from '../components/Layout/Layout';
import apiService from '../services/api';

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 300,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  addButton: {
    backgroundColor: '#2e7d32',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#1b5e20',
    },
  },
  deleteButton: {
    color: '#c62828',
  },
  formControl: {
    marginBottom: theme.spacing(2),
    minWidth: '100%',
  },
  dialogContent: {
    minWidth: 300,
    [theme.breakpoints.up('sm')]: {
      minWidth: 400,
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#666',
  },
  // Mobile card styles
  mobileCard: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(2),
  },
  mobileCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: '1px solid #eee',
  },
  mobileCardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  mobileCardLabel: {
    color: '#666',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  mobileCardValue: {
    fontSize: '0.875rem',
    fontWeight: 500,
  },
}));

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: '#1a1a2e',
    color: '#fff',
    fontWeight: 600,
  },
}))(TableCell);

function Subscriptions() {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedCatalog, setSelectedCatalog] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subsData, usersData, catalogsData] = await Promise.all([
        apiService.getSubscriptions(),
        apiService.getUsers(),
        apiService.getCatalogs(),
      ]);
      setSubscriptions(subsData);
      setUsers(usersData);
      setCatalogs(catalogsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setSnackbar({ open: true, message: 'Error loading data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = () => {
    setSelectedUser('');
    setSelectedCatalog('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleCreateSubscription = async () => {
    if (!selectedUser || !selectedCatalog) {
      setSnackbar({ open: true, message: 'Please select both user and catalog', severity: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      await apiService.createSubscription({
        user_id: selectedUser,
        catalog_id: selectedCatalog,
      });
      setSnackbar({ open: true, message: 'Subscription created successfully', severity: 'success' });
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error creating subscription:', error);
      setSnackbar({ open: true, message: 'Error creating subscription', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSubscription = async (id) => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) {
      return;
    }

    try {
      await apiService.deleteSubscription(id);
      setSnackbar({ open: true, message: 'Subscription deleted successfully', severity: 'success' });
      fetchData();
    } catch (error) {
      console.error('Error deleting subscription:', error);
      setSnackbar({ open: true, message: 'Error deleting subscription', severity: 'error' });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Mobile card component
  const MobileSubscriptionCard = ({ subscription }) => (
    <Card className={classes.mobileCard}>
      <Box className={classes.mobileCardHeader}>
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {subscription.user_name || `User #${subscription.user_id}`}
        </Typography>
        <IconButton
          size="small"
          className={classes.deleteButton}
          onClick={() => handleDeleteSubscription(subscription.id)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box className={classes.mobileCardRow}>
        <Typography className={classes.mobileCardLabel}>Catalog</Typography>
        <Typography className={classes.mobileCardValue}>
          {subscription.catalog_name || `Catalog #${subscription.catalog_id}`}
        </Typography>
      </Box>
      <Box className={classes.mobileCardRow}>
        <Typography className={classes.mobileCardLabel}>Created At</Typography>
        <Typography className={classes.mobileCardValue}>{formatDate(subscription.created_at)}</Typography>
      </Box>
    </Card>
  );

  if (loading) {
    return (
      <Layout title="Subscriptions" onRefresh={fetchData}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Subscriptions" onRefresh={fetchData}>
      <Box className={classes.header}>
        <Typography variant="h6">User-Catalog Subscriptions</Typography>
        <Button
          variant="contained"
          className={classes.addButton}
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Add Subscription
        </Button>
      </Box>

      {subscriptions.length === 0 ? (
        <Paper className={classes.emptyState}>
          <Typography variant="body1">No subscriptions found</Typography>
          <Typography variant="body2" color="textSecondary">
            Click "Add Subscription" to assign a user to a catalog
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Mobile Cards */}
          <Hidden smUp>
            {subscriptions.map((subscription) => (
              <MobileSubscriptionCard key={subscription.id} subscription={subscription} />
            ))}
          </Hidden>

          {/* Desktop Table */}
          <Hidden xsDown>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>ID</StyledTableCell>
                    <StyledTableCell>User</StyledTableCell>
                    <StyledTableCell>Catalog</StyledTableCell>
                    <StyledTableCell>Created At</StyledTableCell>
                    <StyledTableCell align="center">Actions</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscriptions.map((subscription) => (
                    <TableRow key={subscription.id} hover>
                      <TableCell>{subscription.id}</TableCell>
                      <TableCell>{subscription.user_name || `User #${subscription.user_id}`}</TableCell>
                      <TableCell>{subscription.catalog_name || `Catalog #${subscription.catalog_id}`}</TableCell>
                      <TableCell>{formatDate(subscription.created_at)}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          className={classes.deleteButton}
                          onClick={() => handleDeleteSubscription(subscription.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Hidden>
        </>
      )}

      {/* Add Subscription Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Subscription</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel>User</InputLabel>
            <Select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              label="User"
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" className={classes.formControl}>
            <InputLabel>Catalog</InputLabel>
            <Select
              value={selectedCatalog}
              onChange={(e) => setSelectedCatalog(e.target.value)}
              label="Catalog"
            >
              {catalogs.map((catalog) => (
                <MenuItem key={catalog.id} value={catalog.id}>
                  {catalog.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="default">
            Cancel
          </Button>
          <Button
            onClick={handleCreateSubscription}
            color="primary"
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default Subscriptions;
